'use client';

import type { PromotionWideBannerWidgetData } from '@weberus/shared-types';
import type { HomeLogPageNameValue } from '@/app/(pages)/sub-home/[category]/constants';
import { useState, type FC, useMemo } from 'react';

import 'swiper/css/autoplay';
import dynamic from 'next/dynamic';
import { useAtomValue } from 'jotai';
import { clientLogStandardized, YaTrackerImpression } from '@weberus/client-tracker';
import { waitUntilClickTimeout } from '@weberus/utils';
import { PromotionWideBanner as PromotionWideBannerWidget, PromotionWideBannerItem } from '@weberus/ui/components';
import { deviceAtom, pcWidthAtom } from '@/app/_shared/store/device';
import { navigateTo } from '@/app/_shared/utils/action-navigator';

export interface PromotionWideBannerProps {
  data: PromotionWideBannerWidgetData;
  pageNameLog: HomeLogPageNameValue;
}

const PromotionWideBannerModal = dynamic(
  () => import('@weberus/ui/components').then((mod) => mod.PromotionWideBannerModal),
  { ssr: false },
);

/**
 * 위젯_프로모션_배너_가로형
 *
 * @see {@link https://www.figma.com/design/zlHuiOHmYv1ghVDbTHUSvW/%5BGUIDE%5D-%EC%9C%84%EC%A0%AF%2F%EC%98%A4%EB%8D%94%EB%A7%81?node-id=4-13&m=dev&focus-id=7-5122}
 */
export const PromotionWideBanner: FC<PromotionWideBannerProps> = ({ data, pageNameLog }) => {
  const { isPcWidth } = useAtomValue(pcWidthAtom);
  const { deviceType } = useAtomValue(deviceAtom);
  const [isSeeAllModalOpen, setIsSeeAllModalOpen] = useState(false);
  const firstImageRatio = useMemo(() => {
    const firstImage = data.items?.[0]?.items[0]?.image;
    if (firstImage) {
      return { height: firstImage.height, width: firstImage.width };
    }
    return { height: 0, width: 0 };
  }, [data]);
  const openModal = () => {
    setIsSeeAllModalOpen(true);
    clientLogStandardized('click', pageNameLog, data.header.more.logMeta);
  };

  if (!data.items?.[0]?.items.length) {
    return null;
  }
  return (
    <>
      <PromotionWideBannerWidget
        data={data}
        isPcWidth={isPcWidth}
        renderItem={(item, index) => {
          return (
            <div className="w-full">
              <PromotionWideBannerItem
                {...item}
                width={firstImageRatio.width}
                height={firstImageRatio.height}
                isPriority={index <= 1}
                onClick={async (ev) => {
                  ev.preventDefault();
                  await waitUntilClickTimeout(
                    clientLogStandardized('click', pageNameLog, { ...data.logMeta, ...item.logMeta }),
                  );
                  navigateTo(item.action);
                }}
              />
              <YaTrackerImpression
                onImpressed={() => {
                  clientLogStandardized('impression', pageNameLog, { ...data.logMeta, ...item.logMeta });
                }}>
                <div aria-hidden />
              </YaTrackerImpression>
            </div>
          );
        }}
        onClickAll={openModal}
      />

      <PromotionWideBannerModal
        isOpen={isSeeAllModalOpen}
        data={data}
        deviceType={deviceType}
        onClose={() => {
          setIsSeeAllModalOpen(false);
        }}
      />
    </>
  );
};
